import { Box, Popover } from '@cloudscape-design/components';
import React, { KeyboardEvent, useCallback } from 'react';

import { GlossaryTerm } from 'src/types/glossary';

import { renderDefinition } from './renderDefinition';
import './glossary-term-highlight.scss';

interface GlossaryTermHighlightProps {
    /** The surface form of the term as it appears in the content. */
    displayText: string;
    /** The resolved glossary entry (canonical term + definition). */
    entry: GlossaryTerm;
}

/**
 * Inline highlight for a glossary term occurrence.
 *
 * Opens a Cloudscape `Popover` with the term's definition. The Popover is
 * click-triggered to match Meridian/Cloudscape guidance: use Popover (not
 * Tooltip) for anything persistent or that may later contain interactive
 * content such as links. Hover is intentionally not an open trigger.
 *
 * Dismissal mirrors Cloudscape defaults — dismiss button, Escape, or an
 * outside click, all managed by Cloudscape.
 *
 * Accessibility (Requirement 6):
 *   - Tab-focusable in document reading order (`tabIndex={0}`).
 *   - `role="button"` + `aria-haspopup="dialog"` signal to assistive tech
 *     that activating the trigger opens a dialog. `aria-expanded` is
 *     intentionally omitted: Cloudscape's `triggerType="custom"` Popover
 *     manages its own open state and does not expose open/close
 *     callbacks, so any local mirror would silently drift out of sync
 *     on every Cloudscape-driven dismissal and lie about the real state.
 *   - Enter and Space activate the trigger via a synthetic click. Space
 *     also suppresses the default page scroll.
 *   - Cloudscape `Popover` renders the dialog itself with correct focus
 *     management, Escape handling, and the dismiss button.
 *   - Hover and base styles meet WCAG AA contrast (see .scss).
 */
export const GlossaryTermHighlight = ({
    displayText,
    entry,
}: GlossaryTermHighlightProps) => {
    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLSpanElement>) => {
            if (event.key === ' ' || event.key === 'Spacebar') {
                event.preventDefault();
                event.currentTarget.click();
            } else if (event.key === 'Enter') {
                event.preventDefault();
                event.currentTarget.click();
            }
        },
        []
    );

    return (
        <Popover
            dismissButton={true}
            position="bottom"
            size="medium"
            triggerType="custom"
            header={entry.term}
            content={
                <Box variant="p" data-testid="glossary-definition-body">
                    {renderDefinition(entry.definition)}
                </Box>
            }
        >
            <span
                className="glossary-term-highlight"
                role="button"
                tabIndex={0}
                aria-haspopup="dialog"
                data-testid="glossary-term-highlight"
                data-glossary-term={entry.term}
                onKeyDown={handleKeyDown}
            >
                {displayText}
            </span>
        </Popover>
    );
};
